import { CostCenter } from '../models/CostCenter';
import {
  CostCenterBudgetProposal,
  getBudgetProposalStatusLabel
} from '../models/CostCenterBudgetProposal';
import { formatBrazilDateTime } from './datetime.utils';
import { downloadTableAsExcel } from './excel-export.util';

const fmtDate = (value?: string): string => formatBrazilDateTime(value, '');

const fmtBool = (value?: boolean): string => (value === true ? 'Sim' : value === false ? 'Não' : '');

const fmtNum = (value?: number): number | string => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return '';
  return Number(value);
};

export const COST_CENTER_EXPORT_HEADERS = [
  'ID',
  'Código',
  'Nome',
  'Descrição',
  'Tipo',
  'Departamento',
  'Responsável',
  'Centro pai (ID)',
  'Centro pai (nome)',
  'Nível',
  'Orçamento mensal',
  'Orçamento anual',
  'Custo real',
  'Data início',
  'Data fim',
  'Status',
  'Ativo',
  'Aceita custos',
  'Aceita receitas',
  'Moeda',
  'Observações',
  'Qtd. centros filhos',
  'ID gerente',
  'Criado em',
  'Atualizado em',
  'Criado por',
  'Atualizado por'
];

export function buildCostCenterExportRow(cc: CostCenter): (string | number)[] {
  const childCount = Array.isArray(cc.children) ? cc.children.length : cc.hasChildren ? 1 : 0;
  return [
    cc.id ?? '',
    cc.code ?? '',
    cc.name ?? '',
    cc.description ?? '',
    cc.type ?? '',
    cc.department ?? '',
    cc.responsible ?? '',
    cc.parentId ?? '',
    cc.parentName ?? '',
    cc.level ?? '',
    fmtNum(cc.monthlyBudget ?? cc.budget),
    fmtNum(cc.annualBudget),
    fmtNum(cc.actualCost),
    fmtDate(cc.startDate),
    fmtDate(cc.endDate),
    cc.status ?? '',
    fmtBool(cc.isActive),
    fmtBool(cc.acceptsCosts),
    fmtBool(cc.acceptsRevenues),
    cc.currencyCode ?? '',
    cc.notes ?? '',
    childCount,
    cc.managerStaffId ?? '',
    fmtDate(cc.createdAt),
    fmtDate(cc.updatedAt),
    cc.createdBy ?? '',
    cc.updatedBy ?? ''
  ];
}

export const BUDGET_PROPOSAL_EXPORT_HEADERS = [
  'ID',
  'Centro de custo (ID)',
  'Centro de custo (código)',
  'Centro de custo (nome)',
  'Orçamento mensal proposto',
  'Orçamento anual proposto',
  'Justificativa',
  'Solicitado por',
  'Solicitado em',
  'Status',
  'Revisado por',
  'Comentários da revisão',
  'Revisado em'
];

/** Mapa id do colaborador → nome (para exportação). */
export function buildStaffNameMap(
  staff: Array<{ Id?: number; id?: number; FullName?: string; fullName?: string }>
): Map<number, string> {
  const map = new Map<number, string>();
  for (const s of staff) {
    const id = Number(s.Id ?? s.id ?? 0);
    if (id <= 0) continue;
    const name = (s.FullName ?? s.fullName ?? '').trim();
    if (name) {
      map.set(id, name);
    }
  }
  return map;
}

function resolveStaffDisplayName(staffNames: Map<number, string> | undefined, staffId?: number | null): string {
  if (staffId == null || staffId <= 0) {
    return '';
  }
  const name = staffNames?.get(staffId);
  return name || `Colaborador #${staffId}`;
}

export function buildBudgetProposalExportRow(
  p: CostCenterBudgetProposal,
  staffNames?: Map<number, string>
): (string | number)[] {
  return [
    p.id ?? '',
    p.costCenterId ?? '',
    p.costCenterCode ?? '',
    p.costCenterName ?? '',
    fmtNum(p.proposedMonthlyBudget),
    fmtNum(p.proposedAnnualBudget),
    p.justification ?? '',
    resolveStaffDisplayName(staffNames, p.requestedByStaffId),
    fmtDate(p.requestedAt),
    getBudgetProposalStatusLabel(p.status),
    resolveStaffDisplayName(staffNames, p.reviewedByStaffId),
    p.reviewComments ?? '',
    fmtDate(p.reviewedAt)
  ];
}

export function exportCostCentersToExcel(items: CostCenter[], fileNamePrefix = 'centros_de_custo'): void {
  const rows = items.map((cc) => buildCostCenterExportRow(cc));
  const numericColumnIndexes = [0, 7, 9, 10, 11, 12, 21, 22];
  const stamp = new Date().toISOString().slice(0, 10);
  downloadTableAsExcel(`${fileNamePrefix}_${stamp}`, COST_CENTER_EXPORT_HEADERS, rows, [
    ['Exportação completa — Centros de custo'],
    [`Gerado em: ${new Date().toLocaleString('pt-BR')}`],
    [`Total de registros: ${items.length}`]
  ], {
    sheetName: 'Centros de custo',
    numericColumnIndexes
  });
}

export function exportBudgetProposalsToExcel(
  items: CostCenterBudgetProposal[],
  staffNames?: Map<number, string>,
  fileNamePrefix = 'propostas_orcamento'
): void {
  const rows = items.map((p) => buildBudgetProposalExportRow(p, staffNames));
  const numericColumnIndexes = [0, 1, 4, 5];
  const stamp = new Date().toISOString().slice(0, 10);
  downloadTableAsExcel(`${fileNamePrefix}_${stamp}`, BUDGET_PROPOSAL_EXPORT_HEADERS, rows, [
    ['Exportação completa — Propostas de orçamento'],
    [`Gerado em: ${new Date().toLocaleString('pt-BR')}`],
    [`Total de registros: ${items.length}`]
  ], {
    sheetName: 'Propostas',
    numericColumnIndexes
  });
}
